import React, { useEffect, useState } from 'react';
import { Form, Divider, Select, Spin } from 'antd';
import { Space, Text, Button } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';
import { useQuery } from 'react-query';

import { DiscountType, JsonDataItem, type JsonData, type WithOptionalDataTest } from 'types';
import { getDiscountApprovers, getSystemDiscounts, getDiscountReasons } from 'queries';
import { parseErrors, Toast, useDiscountOptions } from 'helpers';
import { AddDiscountModal } from '../add-discount-modal';
import styles from './discount-list.module.scss';

export namespace DiscountList {
  export type Props = {
    job: JsonDataItem | null;
    totalPrice: string;
    onSelect: (discounts: any) => void;
    onLoad: () => void;
  } & WithOptionalDataTest;

  export const $: React.FC<Props> = observer((props) => {
    const { job, onSelect, totalPrice, onLoad } = props;
    const [showDiscountModal, setShowDiscountModal] = useState<boolean>(false);
    const [selectedDiscounts, setSelectedDiscounts] = useState<JsonData>([]);
    const getExistingDiscounts = (discounts: JsonData) => {
      return discounts.map((discount: JsonDataItem) => {
        return {
          discountId: discount.ID,
          approverId: discount.approved_by,
          label: discount.discount_desc,
          amount: discount.amount,
          type: Object.values(DiscountType)[discount.discount_type_ID - 1],
          details: discount,
        };
      });
    };

    const { data: discountReasons, isLoading: isDiscountTypesLoading } = useQuery(
      ['discount-reasons'],
      getDiscountReasons,
      {
        enabled: !!job?.ID,
        onError: (error: JsonDataItem) => {
          Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
        },
      },
    );

    const { data: discountApproverList, isLoading: isApproversLoading } = useQuery(
      ['discount-approvers'],
      getDiscountApprovers,
      {
        enabled: !!job?.ID,
        onError: (error: JsonDataItem) => {
          Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
        },
      },
    );

    const {
      data: systemDiscounts,
      isLoading: isDiscountsLoading,
      refetch: updateCustomerDiscounts,
    } = useQuery(['service-discounts', job?.customer.ID], getSystemDiscounts, {
      enabled: !!job?.ID,
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    const { getLabel } = useDiscountOptions(systemDiscounts);

    const handleAddDiscount = (discount: JsonDataItem) => {
      setShowDiscountModal(false);
      setSelectedDiscounts([...selectedDiscounts, discount]);
    };

    const handleRemoveDiscount = (dsiscountId: number) => {
      setSelectedDiscounts(selectedDiscounts.filter((discount) => discount.discountId !== dsiscountId));
    };

    useEffect(() => {
      onSelect(selectedDiscounts);
    }, [selectedDiscounts]);

    useEffect(() => {
      if (job?.discounts) {
        setSelectedDiscounts(getExistingDiscounts(job?.discounts));
        updateCustomerDiscounts();
      }
    }, [job?.discounts]);

    useEffect(() => {
      onLoad();
    }, []);

    return (
      <Space.$ direction={Space.Direction.Vertical} className={styles.discountList}>
        <Text.$ strong size={Text.Size.LG} className={styles.discountList__title}>
          Discount
        </Text.$>
        {isApproversLoading || isDiscountsLoading || isDiscountTypesLoading ? (
          <Space.$ align={Space.Align.Center} direction={Space.Direction.Vertical}>
            <Spin />
          </Space.$>
        ) : (
          <>
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small} fullWidth>
              <Form.Item name="discounts">
                <div>
                  {selectedDiscounts?.length > 0 &&
                    selectedDiscounts.map((addedDiscount, index) => (
                      <div className={styles.discountList__item} key={index}>
                        <>
                          <Space.$ compact className={styles.discountList__itemField}>
                            <Select
                              value={addedDiscount.discountId}
                              options={[
                                {
                                  label: addedDiscount.label,
                                  value: addedDiscount.discountId,
                                },
                              ]}
                              disabled
                              className={styles.discountList__itemFieldInput}
                            />
                          </Space.$>
                          <div className={styles.discountList__price}>
                            <Text.$ type="danger">
                              -{getLabel(addedDiscount.type, addedDiscount.amount)}
                              {addedDiscount.type === DiscountType.Dollar ? <small> inc GST</small> : ''}
                            </Text.$>
                            <Button.$
                              dataTestId="remove-discount-1"
                              size={Button.Size.Small}
                              type={Button.Type.Link}
                              className={styles.discountList__remove}
                              onClick={() => {
                                handleRemoveDiscount(addedDiscount.discountId);
                              }}
                            >
                              Remove
                            </Button.$>
                          </div>
                        </>
                      </div>
                    ))}
                  <Button.$
                    type={Button.Type.Dashed}
                    block
                    dataTestId="add-another-discount"
                    className={styles.discountList__action}
                    onClick={() => setShowDiscountModal(true)}
                    style={{ marginTop: 8 }}
                  >
                    + Add New
                  </Button.$>
                </div>
              </Form.Item>
            </Space.$>

            {selectedDiscounts.length > 0 && (
              <>
                <Divider />
                <div className={styles.discountList__total}>
                  <Text.$ size={Text.Size.SM}>
                    Total <br />
                    <b>{selectedDiscounts.length}</b> items
                  </Text.$>
                  <Text.$ type="danger">
                    <b>-${totalPrice}</b>
                    <small> inc GST</small>
                  </Text.$>
                </div>
              </>
            )}
          </>
        )}

        <AddDiscountModal.$
          onOk={(discount: JsonDataItem) => handleAddDiscount(discount)}
          onCancel={() => setShowDiscountModal(false)}
          isOpen={showDiscountModal}
          discountList={systemDiscounts}
          selectedDiscounts={selectedDiscounts}
          approversList={discountApproverList?.map((approver: JsonDataItem) => {
            return {
              label: approver.name,
              value: approver.ID?.toString(),
              details: approver,
            };
          })}
          reasons={discountReasons?.map((reason: JsonDataItem) => {
            return {
              label: reason.reason_desc,
              value: reason.ID?.toString(),
              details: reason,
            };
          })}
        />
      </Space.$>
    );
  });
}
